import React, { useEffect, useRef } from 'react';
import { Icon } from '../../icons';
import { IconButton } from './Button';
import './ui.css';

export interface ModalProps {
  title: React.ReactNode;
  onClose: () => void;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  /** CSS width of the dialog; it never exceeds the viewport. */
  width?: number | string;
  /** Close on a backdrop click (default true). Escape always closes. */
  dismissible?: boolean;
  zIndex?: number;
}

export function Modal({ title, onClose, children, footer, width, dismissible = true, zIndex }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useRef(`ui-modal-${Math.random().toString(36).slice(2)}`).current;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    // Move focus into the dialog so keyboard users land in it, and give it back on close.
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => previous?.focus?.();
  }, []);

  return (
    <div
      className="ui-modal-backdrop"
      style={zIndex !== undefined ? { zIndex } : undefined}
      onMouseDown={e => {
        if (dismissible && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="ui-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={width !== undefined ? { width } : undefined}
      >
        <div className="ui-modal__header">
          <h2 id={titleId} className="ui-modal__title">{title}</h2>
          <IconButton label="Close" size="sm" onClick={onClose}>
            <Icon name="clear" />
          </IconButton>
        </div>
        {children !== undefined && <div className="ui-modal__body">{children}</div>}
        {footer !== undefined && <div className="ui-modal__footer">{footer}</div>}
      </div>
    </div>
  );
}
